import { useTranslation } from 'react-i18next';

import { getIn } from 'formik';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import {
  buildSameAsContactFieldArray,
  contactInputEmpty,
  emptyOutFields,
  getValueFromMapping,
  selectWithChildrenContactOnChange
} from '../../../../shared/cardConfig/helpers';
import { CONTACT_FIELDS, SAME_AS_CONTACT_TRANSLATIONS_MAP } from '../../../../shared/fieldConfig/constants';
import { clearFieldError } from '../../../../store/fieldError';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { VALIDATION } from '../../../../utils/validation';
import CreateNew from '../../../CreateNew';
import type { CardDispatch, CardField, CardFieldProps, CardStore } from '../../types';
import CopyShipTo from '../CopyShipTo';
import { checkContactFieldDisable, checkContactSubFields } from '../PackingListContactCard';

const category = CARD_CATEGORIES.coa;

function getEmailConfig({ name = '', t }: CardFieldProps) {
  return {
    field: 'email',
    category: name,
    label: t('shipTo.coa.email.label'),
    type: CONTROL_TYPE.inputWithTags,
    validations: [
      { type: VALIDATION.array, param: [{ type: VALIDATION.cardEmail }] },
      { type: VALIDATION.maxCharacters, param: 255 }
    ],
    onAddTag: ({ store, dispatch }: { store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoice delivery') but an email has been entered, clear the error on the parent
      if (typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    onChange: async (args: Parameters<typeof contactInputEmpty>[0]) => {
      contactInputEmpty(args, name, 'fax', '');
    },
    additionalProps: {
      placeholder: t('shipTo.coa.email.placeholder')
    }
  };
}

function getFaxConfig({ name = '', t }: CardFieldProps) {
  return {
    field: 'fax',
    category: name,
    label: t('shipTo.coa.fax.label'),
    type: CONTROL_TYPE.text,
    validations: [{ type: VALIDATION.numberOfDigits, param: 10 }],
    onBlur: ({ error, store, dispatch }: { error: unknown; store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoiceDelivery') but no error on this field, clear the parent error
      if (!getIn(error, `${category}.fax`) && typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    onChange: async (args: Parameters<typeof contactInputEmpty>[0]) => {
      contactInputEmpty(args, name, 'email', []);
    },
    additionalProps: {
      placeholder: t('shipTo.coa.fax.placeholder')
    }
  };
}

function wrap(fieldWrapper?: string, index: number = -1): string | undefined {
  if (fieldWrapper) {
    return index > -1 ? `${fieldWrapper}[${index}].` : `${fieldWrapper}.`;
  }
}
function SelectWithChildren({
  isReview,
  index = 0,
  fields = [],
  fieldWrapper,
  sameAsFields = [],
  required,
  store
}: CardFieldProps) {
  const { t } = useTranslation();
  let wrapper: string | undefined = '';
  // allows for fields to be included on the root level, nested object, or nested array

  wrapper = wrap(fieldWrapper, index);
  const name = `${wrapper}${category}`;
  const controlConfigMap: Record<string, (props: CardFieldProps) => Record<string, unknown>> = {
    [CONTACT_FIELDS.email]: getEmailConfig,
    [CONTACT_FIELDS.fax]: getFaxConfig
  };
  const { showSubFieldError, isNewContact } = checkContactFieldDisable(name, store);
  const validateContactCardSubFileds = checkContactSubFields(category, store);
  const config = {
    required,
    category,
    parent: `${wrapper}${category}`,
    disabled: isReview && validateContactCardSubFileds,
    name: `${name}.method`,
    contactCardName: `${category}`,
    type: CONTROL_TYPE.selectWithChildren,
    validations: [{ type: VALIDATION.requiredCOA }],
    showSubFieldError,
    validateContactCardSubFileds,
    isNewContact,
    label: t('shipTo.coa.method.label'),
    options: [
      { label: `${t('sameAsContact.none')}`, value: 'N/A' },
      ...sameAsFields.map((sameAsOption: string) => ({
        label: t(SAME_AS_CONTACT_TRANSLATIONS_MAP[sameAsOption]),
        value: sameAsOption
      })),
      {
        value: t('shipTo.coa.newMethod.value'),
        label: t('shipTo.coa.newMethod.label'),
        Comp: CreateNew,
        shouldShowChildren: true
      }
    ],
    initialSelectState: ({ customer }: { customer: CardStore }) => {
      let rootObj: CardStore = customer;
      if (fieldWrapper) {
        const wrappedValue = customer[fieldWrapper];
        rootObj = Array.isArray(wrappedValue) ? wrappedValue : [wrappedValue];
      }
      const coa = index > -1 ? rootObj[index][category] : rootObj[category];
      const mapping = {
        ...sameAsFields.reduce((map: Record<string, string>, sameAsField: string) => {
          map[sameAsField] = sameAsField;
          return map;
        }, {}),
        method: coa && coa.method
      };
      return getValueFromMapping(coa, mapping);
    },
    getReviewFieldValue: ({ customer }: { customer: CardStore }) => {
      const rootObj = fieldWrapper ? customer[fieldWrapper] : customer;
      const coa = index > -1 ? rootObj[index][category] : rootObj[category];
      const mapping = {
        ...sameAsFields.reduce((map: Record<string, string>, sameAsField: string) => {
          map[sameAsField] = t(SAME_AS_CONTACT_TRANSLATIONS_MAP[sameAsField]);
          return map;
        }, {}),
        method: coa && coa.method
      };
      return getValueFromMapping(coa, mapping);
    },
    onChange: async (value: unknown, dispatch: CardDispatch, formik: unknown) => {
      const [fieldsToUpdate, shouldEmptyEmailAndFax] = buildSameAsContactFieldArray(value, sameAsFields);
      await selectWithChildrenContactOnChange({
        dispatch,
        formik,
        fieldsToUpdate,
        category: name
      });
      if (shouldEmptyEmailAndFax) {
        await emptyOutFields(
          [
            { name: `${name}.email`, value: [] },
            { name: `${name}.fax`, value: '' }
          ],
          dispatch,
          formik
        );
      }
    },
    selectChildren: fields.map((fieldProps: CardField) => {
      const configFunc = controlConfigMap[fieldProps.key];
      return configFunc({ t, name, ...fieldProps });
    })
  };

  return <Control {...config} />;
}

interface CertOfAnalysisContactCardProps {
  isReview?: boolean;
  index?: number;
  fields: CardField[];
  fieldWrapper?: string;
  sameAsFields?: string[];
  required?: boolean;
}

function CertOfAnalysisContactCard({
  isReview,
  index = 0,
  fields,
  fieldWrapper,
  sameAsFields = [],
  required,
  ...otherProps
}: CertOfAnalysisContactCardProps) {
  const { t } = useTranslation();

  const cardProps = {
    cardId: category,
    title: t('shipTo.coa.title'),
    subtitle: t('shipTo.coa.subtitle'),
    image: '/food/onboarding/assets/Certificate_Of_Analysis.svg',
    isReview,
    fieldWrapper,
    t
  };

  return (
    <FormCard {...cardProps}>
      {!isReview && index > -1 && <CopyShipTo index={index} category={category} />}
      <SelectWithChildren
        t={t}
        isReview={isReview}
        index={index}
        fields={fields}
        fieldWrapper={fieldWrapper}
        sameAsFields={sameAsFields}
        required={required}
        {...otherProps}
      />
    </FormCard>
  );
}

export default CertOfAnalysisContactCard;
